import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Headphones, Plus, Clock, CheckCircle, MessageSquare,
    Send, Loader2, RefreshCw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    open: { label: 'Abierto', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
    in_progress: { label: 'En Progreso', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
    waiting_client: { label: 'Esperando Respuesta', color: 'bg-sky-500/10 text-sky-400 border-sky-500/30' },
    resolved: { label: 'Resuelto', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
    closed: { label: 'Cerrado', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30' },
};

const priorityConfig = {
    critical: { label: 'Critico', color: 'bg-red-500 text-white' },
    high: { label: 'Alto', color: 'bg-orange-500 text-white' },
    medium: { label: 'Medio', color: 'bg-amber-500/10 text-amber-400' },
    low: { label: 'Bajo', color: 'bg-zinc-500/10 text-zinc-400' },
};

const categoryLabels = {
    bug: 'Error / Bug', feature_request: 'Solicitud de Funcion', billing: 'Facturacion',
    access: 'Acceso / Permisos', performance: 'Rendimiento', other: 'Otro',
};

const moduleOptions = [
    'Vuelos', 'Flota', 'Tripulaciones', 'Despacho', 'Mantenimiento', 'Mtto. Predictivo',
    'CCO', 'Mostrador', 'Rampa', 'Turnaround', 'SMS Seguridad', 'Reportes', 'Revenue',
    'Facturacion', 'Agentes IA', 'Ground Handling', 'Otro',
];

const emptyTicket = {
    subject: '', description: '', category: 'bug', priority: 'medium', module: '',
};

export default function SupportPage() {
    const { token } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [form, setForm] = useState(emptyTicket);
    const [replyText, setReplyText] = useState('');
    const [saving, setSaving] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const fetchTickets = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/support/tickets`, { headers });
            setTickets(res.data);
        } catch { toast.error('Error al cargar tickets'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const createTicket = async () => {
        if (!form.subject.trim()) { toast.error('Asunto requerido'); return; }
        if (!form.description.trim()) { toast.error('Descripcion requerida'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/support/tickets`, form, { headers });
            toast.success('Ticket creado. Nuestro equipo lo atendera pronto.');
            setShowCreate(false);
            setForm(emptyTicket);
            fetchTickets();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear ticket'); }
        finally { setSaving(false); }
    };

    const openTicket = (ticket) => {
        setSelectedTicket(ticket);
        setReplyText('');
    };

    const sendReply = async () => {
        if (!replyText.trim()) { toast.error('Escribe un mensaje'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, { message: replyText }, { headers });
            toast.success('Mensaje enviado');
            setReplyText('');
            const res = await axios.get(`${API}/support/tickets`, { headers });
            setTickets(res.data);
            const updated = res.data.find(t => t.id === selectedTicket.id);
            if (updated) setSelectedTicket(updated);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    const openCount = tickets.filter(t => ['open', 'in_progress', 'waiting_client'].includes(t.status)).length;
    const resolvedCount = tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;

    if (loading) {
        return <div className="space-y-4 animate-fade-in">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />)}</div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="support-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Soporte Tecnico
                    </h1>
                    <p className="text-muted-foreground">Reporta incidencias y solicitudes al equipo AeroOps</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={fetchTickets} data-testid="refresh-support">
                        <RefreshCw className="w-4 h-4 mr-2" />Actualizar
                    </Button>
                    <Button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="new-ticket-btn">
                        <Plus className="w-4 h-4 mr-2" />Nuevo Ticket
                    </Button>
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="total-tickets">{tickets.length}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Total</p>
                </CardContent></Card>
                <Card className="card-aviation border-amber-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-amber-400" data-testid="open-tickets">{openCount}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Activos</p>
                </CardContent></Card>
                <Card className="card-aviation border-emerald-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-emerald-400" data-testid="resolved-tickets">{resolvedCount}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Resueltos</p>
                </CardContent></Card>
            </div>

            {/* Ticket List */}
            <div className="space-y-3">
                {tickets.length === 0 ? (
                    <Card className="card-aviation">
                        <CardContent className="py-12 text-center text-muted-foreground">
                            <Headphones className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p className="font-medium">No tienes tickets de soporte</p>
                            <p className="text-xs mt-1">Si tienes algun problema, crea un ticket y nuestro equipo te ayudara</p>
                        </CardContent>
                    </Card>
                ) : tickets.map(t => {
                    const sc = statusConfig[t.status] || statusConfig.open;
                    const pc = priorityConfig[t.priority] || priorityConfig.medium;
                    const lastMsg = (t.messages || []).slice(-1)[0];
                    const hasStaffReply = (t.messages || []).some(m => m.sender_type === 'staff');
                    return (
                        <Card key={t.id} className={`card-aviation cursor-pointer hover:border-primary/30 transition-colors ${t.status === 'waiting_client' ? 'border-l-4 border-l-sky-500' : t.status === 'open' ? 'border-l-4 border-l-red-500' : ''}`}
                              onClick={() => openTicket(t)} data-testid={`ticket-${t.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <Badge className={pc.color} variant="outline">{pc.label}</Badge>
                                            <Badge className={sc.color} variant="outline">{sc.label}</Badge>
                                            <Badge variant="outline" className="text-[10px]">{categoryLabels[t.category] || t.category}</Badge>
                                            {t.module && <Badge variant="outline" className="text-[10px] font-mono">{t.module}</Badge>}
                                        </div>
                                        <p className="font-semibold text-sm">{t.subject}</p>
                                        {lastMsg && lastMsg.sender_type === 'staff' && (
                                            <p className="text-xs text-sky-400 mt-1 flex items-center gap-1">
                                                <MessageSquare className="w-3 h-3" />Respuesta del equipo AeroOps
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                                        <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(t.created_at).toLocaleDateString('es')}</div>
                                        <p className="font-mono mt-1">{(t.messages || []).length} msgs</p>
                                        {hasStaffReply && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 ml-auto mt-1" />}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Create Ticket Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[520px]" data-testid="create-ticket-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Nuevo Ticket de Soporte</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Asunto *</Label>
                            <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
                                   placeholder="Resumen del problema..." data-testid="ticket-subject" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Categoria</Label>
                                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                                    <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Prioridad</Label>
                                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                                    <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(priorityConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Modulo afectado</Label>
                            <Select value={form.module} onValueChange={v => setForm({...form, module: v})}>
                                <SelectTrigger data-testid="ticket-module"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                                <SelectContent>
                                    {moduleOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Descripcion detallada *</Label>
                            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                                      placeholder="Describe el problema con el mayor detalle posible: que estabas haciendo, que esperabas que pasara, y que paso en su lugar..."
                                      rows={5} data-testid="ticket-description" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createTicket} disabled={saving} className="btn-primary" data-testid="submit-ticket">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                            Enviar Ticket
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Ticket Detail Dialog */}
            <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
                <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto" data-testid="ticket-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase pr-8">
                            {selectedTicket?.subject}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedTicket && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                <Badge className={priorityConfig[selectedTicket.priority]?.color}>{priorityConfig[selectedTicket.priority]?.label}</Badge>
                                <Badge className={statusConfig[selectedTicket.status]?.color}>{statusConfig[selectedTicket.status]?.label}</Badge>
                                <Badge variant="outline" className="text-[10px]">{categoryLabels[selectedTicket.category]}</Badge>
                                {selectedTicket.module && <Badge variant="outline" className="text-[10px] font-mono">{selectedTicket.module}</Badge>}
                                <span className="text-[10px] text-muted-foreground ml-auto">{new Date(selectedTicket.created_at).toLocaleString('es')}</span>
                            </div>

                            {selectedTicket.status === 'resolved' && (
                                <div className="p-3 bg-emerald-500/10 rounded-lg text-sm flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <span className="text-emerald-400 font-medium">Este ticket fue resuelto por el equipo de soporte</span>
                                </div>
                            )}

                            {/* Messages */}
                            <div className="space-y-3 max-h-[350px] overflow-y-auto border rounded-lg p-3 bg-muted/10" data-testid="messages-thread">
                                {(selectedTicket.messages || []).map(msg => (
                                    <div key={msg.id} className={`flex ${msg.sender_type === 'staff' ? 'justify-start' : 'justify-end'}`}>
                                        <div className={`max-w-[85%] rounded-lg p-3 text-sm ${msg.sender_type === 'staff' ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50 border border-border/50'}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-xs">{msg.sender_name}</span>
                                                <Badge variant="outline" className="text-[9px] h-4 px-1.5">
                                                    {msg.sender_type === 'staff' ? 'Soporte AeroOps' : 'Tu'}
                                                </Badge>
                                                <span className="text-[10px] text-muted-foreground ml-auto">{new Date(msg.created_at).toLocaleString('es')}</span>
                                            </div>
                                            <p className="whitespace-pre-wrap">{msg.message}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply (only if not closed) */}
                            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                                <div className="space-y-2 border-t pt-3">
                                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                                              placeholder="Escribe tu respuesta o informacion adicional..." rows={3} data-testid="reply-input" />
                                    <div className="flex justify-end">
                                        <Button onClick={sendReply} disabled={saving} className="btn-primary" data-testid="send-reply">
                                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                            Enviar
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
